import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import {
  AddIcon,
  Button,
  FlexContent,
  InlineOrderEditor,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { type ScrewFamily } from "../../../../http/beamReinforcement/types/screwFamily";
import { useBRScrewFamiliesApi } from "../../../../http/beamReinforcement/useAdminScrewFamilies";
import { PaginationQuery } from "../../../../types";
import ScrewFamilyFormDialog from "./ScrewFamilyFormDialog";

const ScrewFamiliesTab: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true, prefix: "screw_families" });
  const aborter = useRef(new AbortController());
  const screwFamiliesApi = useBRScrewFamiliesApi();
  const dispatch = useContext(NotificationDispatch);
  const [itemToDelete, setItemToDelete] = useState<ScrewFamily>();
  const [itemToEdit, setItemToEdit] = useState<ScrewFamily>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = screwFamiliesApi.useBRScrewFamilies(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);
  const t = i18n.translation.beamReinforcementModule;

  const updateVisibility = async (item: ScrewFamily, visibility: "public" | "internal" | "hidden") => {
    try {
      await screwFamiliesApi.updateBRScrewFamilyVisibility(item.id, { visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await screwFamiliesApi.updateBRScrewFamilyOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) {
    return null;
  }

  return (
    <FlexContent padding>
      <Table<ScrewFamily>
        filters={filters}
        search={{
          placeholder: `${i18n.translation.common.search}...`,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        headingActions={
          <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
            {i18n.translation.common.add}
          </Button>
        }
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.screwDiameter}</TableHeadCell>
            <TableHeadCell align="left">{t.screws.fields.headDiameter}</TableHeadCell>
            <TableHeadCell align="left">{t.screws.fields.yieldMoment}</TableHeadCell>
            <TableHeadCell align="left">{t.screws.fields.tensileCapacity}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
            <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
            <TableHeadCell align="left"></TableHeadCell>
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <TableCell>{item.name}</TableCell>
            <TableCell>
              {item.diameter} {i18n.translation.common.unitMm}
            </TableCell>
            <TableCell>
              {item.headDiameter} {i18n.translation.common.unitMm}
            </TableCell>
            <TableCell>
              {item.yieldMoment} {i18n.translation.common.unitNMm}
            </TableCell>
            <TableCell>
              {item.tensileCapacity} {i18n.translation.common.unitN}
            </TableCell>
            <TableCell>
              <Visibility
                value={item.visibility}
                onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
              />
            </TableCell>
            <TableCell align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </TableCell>
          </>
        )}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        onRowClick={(item) => setItemToEdit(item)}
        total={data?.total}
        error={!!error}
      />

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={t.screwFamilies.singular}
          isSubmitting={isSubmitting}
          buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                setIsSubmitting(true);
                screwFamiliesApi
                  .deleteBRScrewFamily(itemToDelete.id)
                  .then(() => {
                    dispatch(showSuccessNotification(i18n.translation));
                    setItemToDelete(undefined);
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              }}>{i18n.translation.common.yes}</Button>]}
        >
          {i18n.translation.clients.questions.delete}
        </ModalDialog>
      )}

      {isCreateOpen && <ScrewFamilyFormDialog onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <ScrewFamilyFormDialog screwFamily={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </FlexContent>
  );
};

export default ScrewFamiliesTab;
